import Konva from 'konva';
import { Brightness } from 'konva/lib/filters/Brightness';
import { Contrast } from 'konva/lib/filters/Contrast';
import { HSL } from 'konva/lib/filters/HSL';
import { CANVAS_SIZE, fractionPoint, type CardEdit, type Layer } from '@str/shared';

export const PICTURE_FONTS = {
  sans: 'Manrope, Helvetica, Arial, sans-serif',
  serif: "Georgia, 'Times New Roman', serif",
};
export function applyAdjustments(data: ImageData, adjust: CardEdit['adjust']) {
  const context = {
    brightness: () => 1 + adjust.brightness,
    contrast: () => adjust.contrast * 100,
    saturation: () => adjust.saturation,
    hue: () => 0,
    luminance: () => 0,
  } as unknown as Konva.Node;
  if (adjust.brightness && typeof Brightness === 'function') Brightness.call(context, data);
  if (adjust.contrast && typeof Contrast === 'function') Contrast.call(context, data);
  if (adjust.saturation && typeof HSL === 'function') HSL.call(context, data);
  for (let i = 0; i < data.data.length; i += 4) {
    data.data[i] = data.data[i]! + adjust.warmth * 40;
    data.data[i + 2] = data.data[i + 2]! - adjust.warmth * 40;
  }
}
export function pictureNodes(edit: CardEdit, image: HTMLImageElement, width: number) {
  const size = CANVAS_SIZE[edit.canvas];
  const height = (width * size.height) / size.width;
  const crop = edit.crop;
  const photo = new Konva.Image({
    image,
    width,
    height,
    crop: {
      x: crop.x * image.naturalWidth,
      y: crop.y * image.naturalHeight,
      width: crop.w * image.naturalWidth,
      height: crop.h * image.naturalHeight,
    },
    name: 'photo',
  });
  if (Object.values(edit.adjust).some(Boolean)) {
    photo.cache({ pixelRatio: 1 });
    photo.filters([(data) => applyAdjustments(data, edit.adjust)]);
  }
  return [photo, ...edit.layers.map((layer) => layerNode(layer, width, height))];
}
/**
 * The shade under a text block: a gradient from clear above the words to dark below them, wider
 * than the block on every side, so the words read on any photo and move with them as one thing.
 */
export function shadeRect(
  block: { width: number; height: number },
  canvasWidth: number,
  size: number,
) {
  const pad = size * canvasWidth * 1.2;
  const top = pad * 2.2;
  const h = block.height + top + pad;
  return {
    x: -pad,
    y: -top,
    width: block.width + pad * 2,
    height: h,
    cornerRadius: pad * 0.6,
    fillLinearGradientStartPoint: { x: 0, y: 0 },
    fillLinearGradientEndPoint: { x: 0, y: h },
    fillLinearGradientColorStops: [
      0,
      'rgba(0, 0, 0, 0)',
      0.45,
      'rgba(0, 0, 0, 0.42)',
      1,
      'rgba(0, 0, 0, 0.55)',
    ],
    listening: false,
  };
}
function layerNode(layer: Layer, width: number, height: number): Konva.Shape | Konva.Group {
  if (layer.kind === 'path')
    return new Konva.Line({
      id: layer.id,
      points: layer.points.map((p, i) => p * (i % 2 ? height : width)),
      stroke: layer.color,
      strokeWidth: layer.width * width,
      lineCap: 'round',
      lineJoin: 'round',
    });
  const group = new Konva.Group({
    id: layer.id,
    ...fractionPoint(layer, { width, height }),
    rotation: layer.rotate,
  });
  const text = new Konva.Text({
    text: layer.kind === 'text' ? layer.text : layer.char,
    fontFamily:
      layer.kind === 'text'
        ? PICTURE_FONTS[layer.font]
        : 'Apple Color Emoji, Segoe UI Emoji, sans-serif',
    fontSize: layer.size * width,
    fill: layer.kind === 'text' ? layer.color : '#ffffff',
    ...(layer.kind === 'text'
      ? {
          // Bold when the host asked for it; the editors show the same weight they bake.
          fontStyle: layer.weight === 'bold' ? 'bold' : 'normal',
          width: layer.w * width,
          align: layer.align,
          lineHeight: 1.05,
          shadowColor: '#000000',
          shadowOpacity: 0.35,
          shadowBlur: width * 0.035,
          shadowOffsetY: width * 0.004,
        }
      : {}),
  });
  if (layer.kind === 'text' && layer.shade)
    group.add(
      new Konva.Rect(shadeRect({ width: text.width(), height: text.height() }, width, layer.size)),
    );
  if (layer.kind === 'text' && layer.pill) {
    // The pill hugs the longest line with the editor's padding (0.3em across, 0.12em down), so
    // the words sit off its edges exactly as they did on the phone.
    const em = layer.size * width;
    const padX = em * 0.3;
    const padY = em * 0.12;
    const lines = (text as unknown as { textArr?: { width: number }[] }).textArr ?? [];
    const longest = Math.min(text.width(), Math.max(0, ...lines.map((l) => l.width)));
    const left =
      layer.align === 'left'
        ? 0
        : layer.align === 'right'
          ? text.width() - longest
          : (text.width() - longest) / 2;
    group.add(
      new Konva.Rect({
        x: left - padX,
        y: 0,
        width: longest + padX * 2,
        height: text.height() + padY * 2,
        fill: layer.pill,
        cornerRadius: em * 0.35,
      }),
    );
    text.y(padY);
  }
  group.add(text);
  return group;
}
export async function exportPicture(edit: CardEdit, image: HTMLImageElement): Promise<Blob> {
  await document.fonts?.ready;
  const size = CANVAS_SIZE[edit.canvas];
  const container = document.createElement('div');
  const stage = new Konva.Stage({ container, ...size });
  try {
    const layer = new Konva.Layer();
    stage.add(layer);
    layer.add(...pictureNodes(edit, image, size.width));
    stage.draw();
    const blob = await stage.toBlob({ mimeType: 'image/jpeg', quality: 0.92, pixelRatio: 1 });
    if (!blob) throw new Error('Could not export this picture. Try again.');
    return blob;
  } finally {
    stage.destroy();
  }
}

/** The same editable layer nodes as M2, on a transparent delivery-size canvas. */
export async function exportOverlay(layers: Layer[]): Promise<Blob> {
  await document.fonts?.ready;
  const size = CANVAS_SIZE['9:16'];
  const stage = new Konva.Stage({ container: document.createElement('div'), ...size });
  try {
    const canvas = new Konva.Layer();
    stage.add(canvas);
    canvas.add(...layers.map((layer) => layerNode(layer, size.width, size.height)));
    stage.draw();
    const blob = await stage.toBlob({ mimeType: 'image/png', pixelRatio: 1 });
    if (!blob) throw new Error('Could not export the Reel text. Try again.');
    return blob;
  } finally {
    stage.destroy();
  }
}
